<!--

V 0.2.0 - 2026-03-16
   - Modified method updateAngles(), as times stretching over 12 noon,
     e.g. 11:55 to 12:05 where causing the cirlce to color whole circle
    blue, so modified this.

V 0.1.0 - (Created)

-->
<template>
  <div
    class="clock-ring-chart"
    :style="{ width: size + 'px', height: size + 'px', position: 'relative' }"
  >
    <canvas ref="canvas"></canvas>
  </div>
</template>

<script>
import {
  Chart,
  DoughnutController,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js'

Chart.register(DoughnutController, ArcElement, Tooltip, Legend)

export default {
  name: 'ClockRingChart',

  // so these props are what is passed down to this child, from the parent,
  props: {
    startTime: { type: String, required: true },
    endTime: { type: String, required: true },
    size: { type: Number, default: 64 },
    color: { type: String, default: '#42A5F5' },
    background: { type: String, default: '#E0E0E0' },
    duration: { type: Number, default: 600 } // animation length ms
  },

  data () {
    return {
      chart: null,
      currentStart: 0,
      currentEnd: 0,
      targetStart: 0,
      targetEnd: 0,
      animating: false
    }
  },

  mounted () {
    this.initChart()
    this.updateAngles(true) // first load, no animation
  },

  watch: {
    startTime () {
      this.updateAngles(false)
    },
    endTime () {
      this.updateAngles(false)
    }
  },

  methods: {
    parseTime12 (t) {
      let [h, m] = t.split(':').map(Number)
      h = h % 12
      return h + m / 60
    },

    hourToAngle (h) {
      return (h / 12) * Math.PI * 2
    },

    initChart () {
      const ctx = this.$refs.canvas.getContext('2d')

      const baseRing = {
        id: 'baseRing',
        // no-unused-vars
        // eslint-disable-next-line no-unused-vars
        afterDatasetDraw: (chart) => {
          // arc is drawn by highlightArc plugin
          // console.log(chart)
        }
      }

      const highlightArc = {
        id: 'highlightArc',
        afterDraw: (chart) => {
          const { ctx } = chart
          const meta = chart.getDatasetMeta(0)
          const cx = meta.data[0].x
          const cy = meta.data[0].y
          const outer = meta.data[0].outerRadius
          const inner = meta.data[0].innerRadius
          const radius = (outer + inner) / 2

          ctx.save()
          ctx.strokeStyle = this.color
          ctx.lineWidth = outer - inner
          ctx.lineCap = 'round'

          const start = this.currentStart - Math.PI / 2
          const end = this.currentEnd - Math.PI / 2

          ctx.beginPath()

          // Wrap handling: if end < start => go past 360°
          if (end < start) {
            ctx.arc(cx, cy, radius, start, start + Math.PI * 2)
            ctx.arc(cx, cy, radius, -Math.PI / 2, end)
          } else {
            ctx.arc(cx, cy, radius, start, end)
          }

          ctx.stroke()
          ctx.restore()
        }
      }

      const centerTextPlugin = {
        id: 'centerText',
        afterDraw: (chart) => {
          const { ctx, chartArea } = chart
          const x = (chartArea.left + chartArea.right) / 2
          const y = (chartArea.top + chartArea.bottom) / 2

          ctx.save()
          ctx.fillStyle = '#000'
          ctx.font = 'bold 12px Roboto'
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(this.startTime, x, y)
          ctx.restore()
        }
      }

      this.chart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: ['base'],
          datasets: [
            {
              data: [1],
              backgroundColor: [this.background],
              borderWidth: 0
            }
          ]
        },
        options: {
          cutout: '70%',
          responsive: false,
          animation: false,
          plugins: {
            tooltip: { enabled: false },
            legend: { display: false }
          }
        },
        plugins: [baseRing, highlightArc, centerTextPlugin]
      })
    },

    updateAngles (initial = false) {
      const s = this.parseTime12(this.startTime)
      const e = this.parseTime12(this.endTime)

      this.targetStart = this.hourToAngle(s)
      this.targetEnd = this.hourToAngle(e)

      if (this.targetStart > this.targetEnd) {
        this.targetEnd = this.targetStart + this.targetEnd
      }

      // debugger

      // console.log(`Updating angles for ${this.startTime} to ${this.endTime}`);
      // console.log(`Parsed times: start=${s}, end=${e}`);
      // console.log(`Angle values: start=${this.targetStart}, end=${this.targetEnd}`);

      if (initial) {
        this.currentStart = this.targetStart
        this.currentEnd = this.targetEnd
        this.chart.update()
      } else {
        this.animateArc()
      }
    },

    animateArc () {
      if (this.animating) return // avoid overlapping animations
      this.animating = true

      const startStart = this.currentStart
      const startEnd = this.currentEnd
      const endStart = this.targetStart
      const endEnd = this.targetEnd

      const startTime = performance.now()
      const duration = this.duration

      const ease = (t) => t * (2 - t)

      const step = (now) => {
        const progress = Math.min((now - startTime) / duration, 1)
        const eased = ease(progress)

        this.currentStart = startStart + (endStart - startStart) * eased
        this.currentEnd = startEnd + (endEnd - startEnd) * eased

        this.chart.update('none') // no Chart.js animation, only arc updates

        if (progress < 1) {
          requestAnimationFrame(step)
        } else {
          this.animating = false
        }
      }

      requestAnimationFrame(step)
    }
  }
}
</script>

<style scoped>
.clock-ring-chart canvas {
  width: 100% !important;
  height: 100% !important;
}
</style>
